import Lock from 'browser-tabs-lock';
import initWorker from '../initWorker';
import { bufferToBase64UrlEncoded, createCodeVerifierKey, createPromptAuthorizationUri, createRandomString, getLocationPersistenceStateString, getLogoutUrl, parseQuery, parseState, retryPromise, revokeToken, sendMessageToWorker, sha256 } from '../utils';
import { TOKEN_LOCK_KEY, authorizePromptType, errorReason, promptRedirectErrors, subscribeEvents, tokenHintType, workerMessageType } from '../constants';
import { SessionStorage } from '../storage';
import { createAccessTokenCache } from '../tokenCache';
export const createClient = ({ config, redirectStateParameter, persistLocationState = false, cleanCodeParam = true, beforeLoginRequiredRedirect, removeQueryParams, afterAuthentication, beforeRedirect, errorLogout = (_type, logoutUrl)=>window.location.replace(logoutUrl), changeRoute, expiryAdjustmentSeconds, onNewToken, _computeExpiresAt })=>{
    let firstTimeInit = false;
    let refreshTokenError = false;
    const worker = initWorker();
    const lock = new Lock();
    const tokenCache = createAccessTokenCache();
    const tokenSubscribers = [];
    const handleLogout = ()=>{
        window.location.replace(getLogoutUrl(config));
    };
    const getIsRefreshTokenError = ()=>refreshTokenError;
    const getAccessToken = async ({ reqData, init, possiblyRevokedToken } = {})=>{
        if (refreshTokenError) {
            // user must log out when refresh token is invalid
            // so this will prevent any attempt to get new token
            return Promise.reject(errorReason.invalidRefresh);
        }
        if (!firstTimeInit && !init) {
            // tohle zajisti ze muzeme pozadat o token i v pripade ze jeste nemame refresh token z code
            const promise = new Promise((resolve)=>{
                setTimeout(()=>{
                    resolve(getAccessToken({
                        reqData
                    }));
                }, 100);
            });
            return promise;
        }
        // Check the cache before acquiring the lock to avoid the latency of
        // `lock.acquireLock` when the cache is populated.
        const entry = tokenCache.getUnexpiredToken(expiryAdjustmentSeconds);
        if (entry && entry.access_token !== possiblyRevokedToken) {
            return entry.access_token;
        }
        if (await retryPromise(()=>lock.acquireLock(TOKEN_LOCK_KEY, 5000), 10)) {
            try {
                // Check the cache a second time, because it may have been populated
                // by a previous call while this call was waiting to acquire the lock.
                const entry = tokenCache.getUnexpiredToken(expiryAdjustmentSeconds);
                if (entry && entry.access_token !== possiblyRevokedToken) {
                    return entry.access_token;
                }
                const res = await sendMessageToWorker({
                    type: workerMessageType.getToken,
                    config,
                    reqData
                }, worker);
                tokenCache.setToken(res.json, _computeExpiresAt);
                onNewToken?.(res.json.access_token);
                tokenSubscribers.forEach((callback)=>callback(res.json.access_token));
                return res.json.access_token;
            } catch (_e) {
                refreshTokenError = true;
                errorLogout?.(errorReason.invalidRefresh, getLogoutUrl(config));
            } finally{
                await lock.releaseLock(TOKEN_LOCK_KEY);
            }
        }
    };
    const getIsRefreshing = ()=>sendMessageToWorker({
            type: workerMessageType.getIsRefreshing
        }, worker);
    const getAccessTokenFromCache = ()=>tokenCache.getUnexpiredToken(expiryAdjustmentSeconds)?.access_token;
    const getDecodedAccessTokenFromCache = ()=>tokenCache.getUnexpiredToken(expiryAdjustmentSeconds)?.decoded_token;
    const handleLogin = async ()=>{
        const isDevLogin = config.devUsername && config.devPassword;
        const { error, error_description, code, state } = parseQuery(window.location.search) || {};
        const redirectStateSearchParam = redirectStateParameter || state;
        const buildAuthUri = createPromptAuthorizationUri(config, persistLocationState, redirectStateSearchParam);
        if (promptRedirectErrors.includes(error)) {
            const authUri = await buildAuthUri(authorizePromptType.login, state);
            beforeLoginRequiredRedirect?.(error, error_description);
            window.location.replace(authUri);
        } else if (!error) {
            if (code || isDevLogin) {
                if (cleanCodeParam) {
                    removeQueryParams?.('code');
                }
                const code_verifier = SessionStorage.get(createCodeVerifierKey(config.clientId));
                SessionStorage.remove(createCodeVerifierKey(config.clientId));
                await getAccessToken({
                    reqData: {
                        code,
                        code_verifier,
                        username: config.devUsername,
                        password: config.devPassword
                    },
                    init: true
                });
                firstTimeInit = true;
                if (persistLocationState) {
                    const locationState = parseState(state);
                    if (locationState?.originatedFromApp) {
                        changeRoute?.(locationState);
                    }
                }
                afterAuthentication?.();
            } else {
                beforeRedirect?.();
                const code_verifier = createRandomString();
                const code_challengeBuffer = await sha256(code_verifier);
                const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
                SessionStorage.save(createCodeVerifierKey(config.clientId), code_verifier);
                const authUri = await buildAuthUri(authorizePromptType.none, getLocationPersistenceStateString(redirectStateSearchParam, window.location), {
                    code_challenge,
                    code_challenge_method: 'S256'
                });
                window.location.replace(authUri);
            }
        } else {
            errorLogout?.(errorReason.generic, getLogoutUrl(config), error);
        }
    };
    const handleRevoke = async ()=>{
        await revokeToken({
            ...config,
            tokenType: tokenHintType.accessToken,
            token: getAccessTokenFromCache()
        });
        await sendMessageToWorker({
            type: workerMessageType.revokeToken,
            config
        }, worker);
    };
    const on = (event, callback)=>{
        if (event === subscribeEvents.token) {
            tokenSubscribers.push(callback);
            callback(getAccessTokenFromCache());
            return ()=>{
                const index = tokenSubscribers.indexOf(callback);
                if (index > -1) {
                    tokenSubscribers.splice(index, 1);
                }
            };
        }
    };
    return {
        handleLogin,
        handleRevoke,
        handleLogout,
        getAccessToken,
        getAccessTokenFromCache,
        getDecodedAccessTokenFromCache,
        getIsRefreshing,
        getIsRefreshTokenError,
        on,
        getExpiresAt: tokenCache.getExpiresAt
    };
};
